import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { routePathConfig } from 'src/routes/config';
import type { ErrorLocationState } from 'src/features/error/errorCodes';
import { OAUTH_PARAMS, PKCE_METHODS } from 'src/constants/oauth';

/**
 * Route-level guard for endpoints that REQUIRE `client_id` and `redirect_uri`
 * query parameters (i.e. `/login` and `/grantConsent`). Without these the entire
 * Pitta OAuth-like flow is undefined, so we redirect to `/error` before mounting
 * the protected page.
 *
 * Place children inside `<Route element={<ClientParamsGuard />}>` so React Router
 * renders `<Outlet />` only when params exist.
 */
export const ClientParamsGuard = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const clientId = searchParams.get(OAUTH_PARAMS.CLIENT_ID);
  const redirectUri = searchParams.get(OAUTH_PARAMS.REDIRECT_URI);
  const codeChallenge = searchParams.get(OAUTH_PARAMS.CODE_CHALLENGE);
  const codeChallengeMethod = searchParams.get(OAUTH_PARAMS.CODE_CHALLENGE_METHOD);

  const isGrantConsentRoute = location.pathname === routePathConfig.grantConsent;
  const isOAuthFlow = Boolean(clientId) || Boolean(redirectUri) || isGrantConsentRoute;

  if (isOAuthFlow) {
    const hasRequiredParams =
      Boolean(clientId) &&
      Boolean(redirectUri) &&
      Boolean(codeChallenge) &&
      codeChallengeMethod === PKCE_METHODS.S256;

    if (!hasRequiredParams) {
      const errorState: ErrorLocationState = { code: 'MISSING_PARAMS' };
      return <Navigate to={routePathConfig.error} state={errorState} replace />;
    }
  }

  return <Outlet />;
};
